import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { getApiErrorMessage } from "utils/errors";

const parseApiError = async (response) => {
  const message = response.errorMessage || "Error";
  const status = response.status;
  let code = null;

  try {
    const errorData = await response.clone().json();
    code = errorData?.code ?? null;
  } catch {
    // The centralized response.errorMessage already handles non-JSON bodies.
  }

  return { message, status, code };
};

export const validateInvitationToken = createAsyncThunk(
  "invitation/validateToken",
  async (token, { rejectWithValue }) => {
    try {
      const response = await callApi(`/invitation/validate/${token}`, "GET");
      
      if (!response.ok) {
        const { message, status, code } = await parseApiError(
          response,
          "This invitation is invalid or has expired. Please request a new invitation."
        );
        return rejectWithValue({ message, status, code });
      }
      
      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          
          if (data && typeof data === "object" && (data.ok === false || data.error)) {
            const message = getApiErrorMessage(data, "Error");
            return rejectWithValue({ message, status: response.status, code: data?.code || null });
          }
          
          if (data && typeof data === "object" && data.valid !== true && data.valid !== undefined) {
            const message = getApiErrorMessage(data, "Error");
            return rejectWithValue({ message, status: response.status, code: data?.code || null });
          }
          
          return data;
        }
        return true;
      } catch (parseError) {
        return true;
      }
    } catch (err) {
      return rejectWithValue({
        message: err?.message || "Error",
        status: 0,
        code: null,
      });
    }
  }
);

export const getGoogleInvitationRedirect = createAsyncThunk(
  "invitation/getGoogleInvitationRedirect",
  async (token, { rejectWithValue }) => {
    if (!token) {
      return rejectWithValue({
        message: "Missing invitation token",
        status: 0,
        code: null,
      });
    }

    try {
      const response = await callApi(
        `/login/google/?invitation_token=${encodeURIComponent(token)}`,
        "GET"
      );

      if (!response.ok) {
        const { message, status, code } = await parseApiError(
          response,
          "Failed to get Google redirect URL"
        );
        return rejectWithValue({ message, status, code });
      }

      let data;
      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          data = await response.json();
        } else {
          const text = await response.text();
          if (text && text.trim()) {
            try {
              data = JSON.parse(text);
            } catch {
              throw new Error("Response is not valid JSON");
            }
          } else {
            throw new Error("Empty response");
          }
        }
      } catch (parseError) {
        console.error("Error parsing Google redirect response:", parseError);
        return rejectWithValue({
          message: "Invalid response from server",
          status: response.status,
          code: null,
        });
      }

      if (!data.redict_url || typeof data.redict_url !== "string" || !data.redict_url.trim()) {
        return rejectWithValue({
          message: "Google redirect URL not provided",
          status: response.status,
          code: null,
        });
      }

      return { redict_url: data.redict_url };
    } catch (err) {
      return rejectWithValue({
        message: err?.message || "Error",
        status: 0,
        code: null,
      });
    }
  }
);

export const signupFromInvitation = createAsyncThunk(
  "invitation/signupFromInvitation",
  async (payload, { rejectWithValue }) => {
    try {
      const fd = new FormData();

      fd.append("token", payload.token);
      fd.append("first_name", payload.firstName);
      fd.append("last_name", payload.lastName);
      fd.append("password", payload.password);

      if (payload.phone) {
        fd.append("phone", payload.phone);
      }

      if (payload.avatarFile) {
        fd.append("picture", payload.avatarFile);
      }

      const response = await callApi(
        "/invitation/complete",
        "POST",
        fd,
        { contentType: "multipart/form-data" }
      );

      if (!response.ok) {
        const { message, status, code } = await parseApiError(
          response,
          "Failed to complete invitation."
        );
        return rejectWithValue({ message, status, code });
      }

      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          
          if (data && typeof data === "object" && (data.ok === false || data.error)) {
            const message = getApiErrorMessage(data, "Error");
            return rejectWithValue({ message, status: response.status, code: data?.code || null });
          }
          
          return data;
        }
        return true;
      } catch (parseError) {
        return true;
      }
    } catch (err) {
      return rejectWithValue({
        message: err?.message || "Error",
        status: 0,
        code: null,
      });
    }
  }
);

const invitationSlice = createSlice({
  name: "invitation",
  initialState: {
    validateStatus: "idle",
    validateError: null,
    validateData: null,
    googleRedirectStatus: "idle",
    googleRedirectError: null,
    googleRedirectUrl: null,
    signupStatus: "idle",
    signupError: null,
    signupData: null,
  },
  reducers: {
    clearValidateError(state) {
      state.validateError = null;
      state.validateStatus = "idle";
      state.validateData = null;
    },
    clearGoogleRedirect(state) {
      state.googleRedirectStatus = "idle";
      state.googleRedirectError = null;
      state.googleRedirectUrl = null;
    },
    clearSignupError(state) {
      state.signupStatus = "idle";
      state.signupError = null;
      state.signupData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateInvitationToken.pending, (state) => {
        state.validateStatus = "loading";
        state.validateError = null;
        state.validateData = null;
      })
      .addCase(validateInvitationToken.fulfilled, (state, action) => {
        state.validateStatus = "succeeded";
        state.validateError = null;
        state.validateData = action.payload;
      })
      .addCase(validateInvitationToken.rejected, (state, action) => {
        state.validateStatus = "failed";
        state.validateError = action.payload || {
          message: "This invitation is invalid or has expired. Please request a new invitation.",
          status: 0,
          code: null,
        };
        state.validateData = null;
      })
      .addCase(getGoogleInvitationRedirect.pending, (state) => {
        state.googleRedirectStatus = "loading";
        state.googleRedirectError = null;
        state.googleRedirectUrl = null;
      })
      .addCase(getGoogleInvitationRedirect.fulfilled, (state, action) => {
        state.googleRedirectStatus = "succeeded";
        state.googleRedirectError = null;
        state.googleRedirectUrl = action.payload.redict_url;
      })
      .addCase(getGoogleInvitationRedirect.rejected, (state, action) => {
        state.googleRedirectStatus = "failed";
        state.googleRedirectError = action.payload || {
          message: "Failed to get Google redirect URL",
          status: 0,
          code: null,
        };
        state.googleRedirectUrl = null;
      })
      .addCase(signupFromInvitation.pending, (state) => {
        state.signupStatus = "loading";
        state.signupError = null;
        state.signupData = null;
      })
      .addCase(signupFromInvitation.fulfilled, (state, action) => {
        state.signupStatus = "succeeded";
        state.signupError = null;
        state.signupData = action.payload;
      })
      .addCase(signupFromInvitation.rejected, (state, action) => {
        state.signupStatus = "failed";
        state.signupError = action.payload || {
          message: "Failed to complete invitation.",
          status: 0,
          code: null,
        };
        state.signupData = null;
      });
  },
});

export const { clearValidateError, clearGoogleRedirect, clearSignupError } = invitationSlice.actions;
export default invitationSlice.reducer;
